/**
 * RunbooksPage
 *
 * Operational runbooks for troubleshooting and maintenance procedures.
 *
 * @module help
 * @spec 102-screen-completeness
 * @story US4 - Support Screens
 * @requirement FR-023
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ScrollText,
  Search,
  ChevronRight,
  AlertTriangle,
  Clock,
  Tag,
  Filter,
  AlertCircle,
} from 'lucide-react';
import { ComingSoonBanner } from '../../components/ui/ComingSoonBanner';
import { supportHelp } from '../../data/help/supportHelp';
import { PageLayout, Card, PageHelpPanel, AppTooltip } from '../../components/ui';

export type RunbookSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface Runbook {
  id: string;
  title: string;
  description: string;
  category: string;
  severity: RunbookSeverity;
  estimatedTime: number; // in minutes
  lastUpdated: string;
  tags: string[];
}

export interface RunbooksPageProps {
  /** Runbooks - undefined triggers coming soon state */
  runbooks?: Runbook[];
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  error?: Error | null;
  /** Callback for search */
  onSearch?: (query: string) => void;
}

const SEVERITY_STYLES: Record<RunbookSeverity, { bg: string; text: string; border: string }> = {
  critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  high: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  medium: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  low: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
};

const COMMON_RUNBOOKS = [
  { title: 'Capacity Health Check', path: '/help/runbooks/capacity-health-check', severity: 'medium' as RunbookSeverity },
  { title: 'Database Connection Issues', path: '/help/runbooks/db-connection-issues', severity: 'critical' as RunbookSeverity },
  { title: 'SLA Breach Response', path: '/help/runbooks/sla-breach-response', severity: 'high' as RunbookSeverity },
  { title: 'Backup Failure Recovery', path: '/help/runbooks/backup-failure-recovery', severity: 'high' as RunbookSeverity },
];

const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-12 w-96 rounded-lg bg-[var(--color-bg-subtle)]" />
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-24 rounded-lg bg-[var(--color-bg-subtle)]" />
      ))}
    </div>
  </div>
);

const RunbookCard: React.FC<{ runbook: Runbook }> = ({ runbook }) => {
  const severityStyle = SEVERITY_STYLES[runbook.severity];

  return (
    <Link to={`/help/runbooks/${runbook.id}`} className="block">
      <Card isHoverable className="transition-all hover:border-[var(--color-accent)]">
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <span className={`rounded-full border px-2 py-0.5 text-xs font-medium uppercase ${severityStyle.bg} ${severityStyle.text} ${severityStyle.border}`}>
                {runbook.severity}
              </span>
              <span className="text-xs text-[var(--color-text-secondary)]">
                {runbook.category}
              </span>
            </div>
            <h3 className="font-medium text-[var(--color-text-primary)]">
              {runbook.title}
            </h3>
            <p className="mt-1 line-clamp-2 text-sm text-[var(--color-text-secondary)]">
              {runbook.description}
            </p>
          </div>
          <ChevronRight className="ml-4 h-5 w-5 flex-shrink-0 text-[var(--color-text-secondary)]" />
        </div>

        <div className="mt-4 flex items-center gap-4 text-sm text-[var(--color-text-secondary)]">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>{runbook.estimatedTime} min</span>
          </div>
          {runbook.tags.slice(0, 3).map((tag) => (
            <div key={tag} className="flex items-center gap-1">
              <Tag className="h-3 w-3" />
              <span>{tag}</span>
            </div>
          ))}
        </div>
      </Card>
    </Link>
  );
};

export const RunbooksPage: React.FC<RunbooksPageProps> = ({
  runbooks,
  isLoading = false,
  error,
  onSearch,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<RunbookSeverity | 'all'>('all');

  const filteredRunbooks = runbooks?.filter((runbook) => {
    const matchesSearch =
      runbook.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      runbook.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      runbook.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesSeverity = severityFilter === 'all' || runbook.severity === severityFilter;
    return matchesSearch && matchesSeverity;
  });

  // Show coming soon if no data
  if (!runbooks && !isLoading && !error) {
    return (
      <PageLayout title="Runbooks" icon={<ScrollText className="h-6 w-6" />}>
        <ComingSoonBanner
          title="Operational Runbooks"
          description="Step-by-step procedures for troubleshooting, maintenance, and incident response. Browse runbooks by category and severity."
          expectedAvailability="Phase 4 Implementation"
          relatedLinks={[
            { label: 'Documentation', path: '/help/docs' },
            { label: 'Support', path: '/help/support' },
          ]}
        />

        {/* Show common runbooks placeholder */}
        <div className="mt-8">
          <h2 className="mb-4 text-lg font-semibold text-[var(--color-text-primary)]">
            Common Runbooks
          </h2>
          <div className="space-y-3">
            {COMMON_RUNBOOKS.map((runbook) => {
              const severityStyle = SEVERITY_STYLES[runbook.severity];
              return (
                <Link key={runbook.path} to={runbook.path} className="block">
                  <Card isHoverable className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium uppercase ${severityStyle.bg} ${severityStyle.text}`}>
                        {runbook.severity}
                      </span>
                      <span className="font-medium text-[var(--color-text-primary)]">
                        {runbook.title}
                      </span>
                    </div>
                    <ChevronRight className="h-5 w-5 text-[var(--color-text-secondary)]" />
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="Runbooks" icon={<ScrollText className="h-6 w-6" />}>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <h2 className="text-lg font-medium text-red-800 dark:text-red-200">
              Failed to load runbooks
            </h2>
          </div>
          <p className="mt-2 text-sm text-red-700 dark:text-red-300">
            {error.message}
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Runbooks"
      description="Operational procedures and troubleshooting guides"
      icon={<ScrollText className="h-6 w-6" />}
      actions={<PageHelpPanel content={supportHelp} buttonSize="sm" />}
    >
      <div data-testid="runbooks-page">
        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Search and Filter */}
            <div className="mb-6 flex flex-wrap items-center gap-4">
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-secondary)]" />
                <input
                  type="text"
                  placeholder="Search runbooks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] py-2 pl-10 pr-4 text-[var(--color-text-primary)]"
                />
              </div>

              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-[var(--color-text-secondary)]" />
                <AppTooltip title="Filter runbooks by severity level">
                  <select
                    value={severityFilter}
                    onChange={(e) => setSeverityFilter(e.target.value as RunbookSeverity | 'all')}
                    className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 py-2 text-[var(--color-text-primary)]"
                  >
                    <option value="all">All Severities</option>
                    <option value="critical">Critical</option>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                </AppTooltip>
              </div>
            </div>

            {/* Critical Alert */}
            {runbooks?.some((r) => r.severity === 'critical') && (
              <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <span className="font-medium text-red-800 dark:text-red-200">
                    {runbooks.filter((r) => r.severity === 'critical').length} critical runbooks available
                  </span>
                </div>
              </div>
            )}

            {/* Runbooks List */}
            <div className="space-y-4">
              {filteredRunbooks && filteredRunbooks.length > 0 ? (
                filteredRunbooks.map((runbook) => (
                  <RunbookCard key={runbook.id} runbook={runbook} />
                ))
              ) : (
                <div className="py-12 text-center text-[var(--color-text-secondary)]">
                  <ScrollText className="mx-auto mb-4 h-12 w-12 opacity-50" />
                  <p className="text-lg font-medium">No runbooks found</p>
                  <p className="mt-1 text-sm">
                    {searchQuery || severityFilter !== 'all'
                      ? 'Try adjusting your search or filters'
                      : 'No runbooks have been created yet'}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default RunbooksPage;
